import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import ProFeatureDialog from '@/components/ProFeatureDialog';
import { MY_CPM_LEADERBOARD_PRIVACY, SET_MY_CPM_LEADERBOARD_ANONYMOUS } from '@/queries/queries';
import { resolveLeaderboardAnonymousToggle } from '@/lib/cpm/resolveLeaderboardAnonymousToggle';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

type PrivacyResult = {
  myCpmLeaderboardPrivacy: {
    anonymous: boolean;
    can_remain_anonymous: boolean;
  };
};

export function LeaderboardAnonymousToggle({
  variant,
  onChanged,
}: {
  variant: 'compact' | 'settings';
  onChanged?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const { data, loading, error, refetch } = useQuery<PrivacyResult>(MY_CPM_LEADERBOARD_PRIVACY, {
    errorPolicy: 'all',
  });
  const [setAnonymous, { loading: saving }] = useMutation(SET_MY_CPM_LEADERBOARD_ANONYMOUS);

  const privacy = data?.myCpmLeaderboardPrivacy;
  if (loading && !privacy) return null;
  if (error || !privacy) return null;

  const { checked, requiresUpgrade } = resolveLeaderboardAnonymousToggle({
    canRemainAnonymous: privacy.can_remain_anonymous,
    preference: privacy.anonymous,
  });

  const onCheckedChange = async (next: boolean) => {
    if (requiresUpgrade && next) {
      setUpgradeOpen(true);
      return;
    }
    if (!privacy.can_remain_anonymous) return;
    try {
      await setAnonymous({ variables: { anonymous: next } });
      await refetch();
      onChanged?.();
      toast({ title: t('cpmLeaderboard.privacySaved') });
    } catch {
      toast({
        title: t('cpmLeaderboard.privacyError'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div
      data-testid="cpm-leaderboard-anonymous-toggle"
      className={cn(
        variant === 'compact' && 'flex items-center justify-between gap-3 px-3 py-2 border-t border-primary/10 bg-card',
        variant === 'settings' && 'space-y-2',
      )}
    >
      <div className={cn('flex items-center gap-3 min-w-0', variant === 'settings' && 'w-full justify-between')}>
        <div className="min-w-0">
          <Label htmlFor={`cpm-leaderboard-anonymous-${variant}`} className="cursor-pointer text-sm">
            {t('cpmLeaderboard.remainAnonymous')}
          </Label>
          {variant === 'settings' && (
            <p className="text-xs text-muted-foreground mt-1">
              {t('cpmLeaderboard.remainAnonymousHint')}
            </p>
          )}
        </div>
        <Switch
          id={`cpm-leaderboard-anonymous-${variant}`}
          checked={checked}
          disabled={saving}
          onCheckedChange={onCheckedChange}
          aria-label={t('cpmLeaderboard.remainAnonymous')}
        />
      </div>
      {upgradeOpen && (
        <ProFeatureDialog
          open={upgradeOpen}
          onOpenChange={setUpgradeOpen}
          onUpgrade={() => {
            setUpgradeOpen(false);
            navigate('/plans');
          }}
          title={t('cpmLeaderboard.upgradeTitle')}
          body={t('cpmLeaderboard.upgradeBody')}
          dismissLabel={t('cpmLeaderboard.upgradeDismiss')}
          upgradeLabel={t('cpmLeaderboard.upgradeCta')}
          testIdPrefix="leaderboard-anonymous-upgrade"
        />
      )}
    </div>
  );
}
